"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.catalogSnapshotCreators = exports.catalogSnapshotOverriders = exports.catalogSnapshotFilters = exports.isCatalogSnapshotWithHubrise = void 0;
const connector_interface_1 = require("./connector.interface");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
function fromArrayToMap(data, key) {
    return (data || []).reduce((acc, item) => {
        acc[item[key]] = item;
        return acc;
    }, {});
}
const createTags = (catalogSnapshot) => {
    if (!catalogSnapshot) {
        return [];
    }
    return catalogSnapshot.data.tags
        .map(tag => {
        const childrens = catalogSnapshot.data.tags.filter(childrenTag => childrenTag.parentTagId === tag._id);
        return {
            ...tag,
            childrens
        };
    });
};
const createProductBuildMaps = (catalogSnapshot) => ({
    productsAsMap: fromArrayToMap(catalogSnapshot.data.products, "_id"),
    variationsAsMap: fromArrayToMap(catalogSnapshot.data.variations, "_id"),
    ingredientAsMap: fromArrayToMap(catalogSnapshot.data.ingredients, "_id"),
    optionsAsMap: fromArrayToMap(catalogSnapshot.data.options, "_id"),
});
const createPackBuildMaps = (catalogSnapshot) => ({
    ...createProductBuildMaps(catalogSnapshot),
    stepsAsMap: fromArrayToMap(catalogSnapshot.data.steps, "_id"),
    rawProductVariationsAsMap: fromArrayToMap(catalogSnapshot.data.products.flatMap(product => product.productVariations), "_id"),
});
const buildProductVariation = (rawProductVariation, maps) => {
    const product = maps.productsAsMap[rawProductVariation.productId];
    (0, tiny_invariant_1.default)(product, "Product not found");
    const variation = maps.variationsAsMap[rawProductVariation.variationId];
    return {
        ...rawProductVariation,
        variation,
        description: rawProductVariation.description || product.description,
        productVariationToIngredients: rawProductVariation.productVariationToIngredients.map(productVariationToIngredient => {
            const ingredient = maps.ingredientAsMap[productVariationToIngredient.ingredientId];
            (0, tiny_invariant_1.default)(ingredient, "Ingredient not found");
            return {
                ...productVariationToIngredient,
                ingredient,
            };
        }),
    };
};
function pickInMapOrder(ids, map) {
    const idsSet = new Set(ids);
    return Object.keys(map)
        .filter(key => idsSet.has(key))
        .map(key => map[key]);
}
const buildOption = (rawOption, maps) => ({
    ...rawOption,
    products: pickInMapOrder(rawOption.productsIds, maps.productsAsMap)
        .map(rawProduct => buildProduct(rawProduct, maps, { withOptions: false })),
});
const buildProduct = (rawProduct, maps, { withOptions = true } = {}) => ({
    ...rawProduct,
    options: withOptions
        ? pickInMapOrder(rawProduct.optionsIds, maps.optionsAsMap)
            .map(rawOption => buildOption(rawOption, maps))
        : [],
    productVariations: rawProduct.productVariations.map(productVariation => buildProductVariation(productVariation, maps)),
});
const buildPackProductVariation = (productVariationId, maps) => {
    const rawProductVariation = maps.rawProductVariationsAsMap[productVariationId];
    (0, tiny_invariant_1.default)(rawProductVariation, "Product variation not found");
    return buildProductVariation(rawProductVariation, maps);
};
const buildPackToStep = (packToStep, maps) => {
    const step = maps.stepsAsMap[packToStep.stepId];
    (0, tiny_invariant_1.default)(step, "Step not found");
    return {
        ...packToStep,
        step: {
            ...step,
            stepToProductVariations: step.stepToProductVariations.map(stepToProductVariation => ({
                ...stepToProductVariation,
                productVariation: buildPackProductVariation(stepToProductVariation.productVariationId, maps),
            })),
        },
    };
};
const buildPack = (rawPack, maps) => ({
    ...rawPack,
    packToProductVariations: rawPack.packToProductVariations.map(packToProductVariation => ({
        ...packToProductVariation,
        productVariation: buildPackProductVariation(packToProductVariation.productVariationId, maps),
    })),
    packToSteps: rawPack.packToSteps.map(packToStep => buildPackToStep(packToStep, maps)),
});
const createProductVariations = (catalogSnapshot) => {
    if (!catalogSnapshot) {
        return [];
    }
    const maps = createProductBuildMaps(catalogSnapshot);
    return catalogSnapshot.data.products
        .flatMap(product => product.productVariations)
        .map(productVariation => buildProductVariation(productVariation, maps));
};
const createOptions = (catalogSnapshot) => {
    if (!catalogSnapshot) {
        return [];
    }
    const maps = createProductBuildMaps(catalogSnapshot);
    return catalogSnapshot.data.options.map(option => buildOption(option, maps));
};
const createProducts = (catalogSnapshot) => {
    if (!catalogSnapshot) {
        return [];
    }
    const maps = createProductBuildMaps(catalogSnapshot);
    return catalogSnapshot.data.products.map(product => buildProduct(product, maps));
};
const createProduct = (catalogSnapshot, productId) => {
    if (!catalogSnapshot) {
        return undefined;
    }
    const maps = createProductBuildMaps(catalogSnapshot);
    const rawProduct = maps.productsAsMap[productId];
    if (!rawProduct) {
        return undefined;
    }
    return buildProduct(rawProduct, maps);
};
const createPacks = (catalogSnapshot) => {
    if (!catalogSnapshot) {
        return [];
    }
    const maps = createPackBuildMaps(catalogSnapshot);
    return catalogSnapshot.data.packs.map(pack => buildPack(pack, maps));
};
const createPack = (catalogSnapshot, packId) => {
    if (!catalogSnapshot) {
        return undefined;
    }
    const rawPack = catalogSnapshot.data.packs.find(pack => pack._id === packId);
    if (!rawPack) {
        return undefined;
    }
    const maps = createPackBuildMaps(catalogSnapshot);
    return buildPack(rawPack, maps);
};
const createPromotions = (catalogSnapshot) => {
    if (!catalogSnapshot) {
        return [];
    }
    return catalogSnapshot.data.promotions;
};
const createPromotion = (catalogSnapshot, promotionId) => {
    if (!catalogSnapshot) {
        return undefined;
    }
    return catalogSnapshot.data.promotions.find(promotion => promotion._id === promotionId);
};
const overrideProductVariation = ({ productVariation, catalogSnapshotToShop }) => {
    const productVariationOverride = catalogSnapshotToShop?.data?.productsOverrides.find(productsOverride => productsOverride.productId === productVariation.productId)?.productVariations.find(productVariationOverride => productVariationOverride.productVariationId === productVariation._id);
    if (!productVariationOverride) {
        return productVariation;
    }
    return {
        ...productVariation,
        disabled: productVariationOverride?.disabled ?? productVariation?.disabled,
        price: productVariationOverride?.price ?? productVariation?.price,
        deliveryPrice: productVariationOverride?.deliveryPrice ?? productVariationOverride?.price ?? productVariation?.deliveryPrice,
        takeAwayPrice: productVariationOverride?.takeAwayPrice ?? productVariationOverride?.price ?? productVariation?.takeAwayPrice,
        onSpotPrice: productVariationOverride?.onSpotPrice ?? productVariationOverride?.price ?? productVariation?.onSpotPrice,
    };
};
/**
 * Les produits d'option (« suppléments ») portés par `product.options[].products[]`
 * sont des produits à part entière : leurs surcharges par shop (prix, désactivation)
 * doivent s'appliquer même quand on les lit à travers leur produit parent.
 */
const overrideOptions = ({ options, catalogSnapshotToShop }) => {
    return options.map(option => ({
        ...option,
        products: (option.products || []).map(product => overrideProduct({ product, catalogSnapshotToShop })),
    }));
};
const overrideProduct = ({ product, catalogSnapshotToShop }) => {
    const hasOverrides = (catalogSnapshotToShop?.data?.productsOverrides?.length ?? 0) > 0;
    if (!hasOverrides) {
        return product;
    }
    const productsOverride = catalogSnapshotToShop.data.productsOverrides.find(productsOverride => productsOverride.productId === product._id);
    const options = (product.options?.length ?? 0) > 0
        ? overrideOptions({ options: product.options, catalogSnapshotToShop })
        : product.options;
    if (!productsOverride) {
        return options === product.options
            ? product
            : { ...product, options };
    }
    return {
        ...product,
        disabled: productsOverride.disabled ?? product?.disabled,
        productVariations: product.productVariations.map(productVariation => overrideProductVariation({ productVariation, catalogSnapshotToShop })),
        options,
    };
};
const overridePackToProductVariation = ({ packToProductVariation, catalogSnapshotToShop }) => {
    const productOverride = catalogSnapshotToShop?.data?.productsOverrides.find(productsOverride => productsOverride.productId === packToProductVariation.productVariation.productId);
    const productVariationOverride = productOverride?.productVariations.find(productVariationOverride => productVariationOverride.productVariationId === packToProductVariation.productVariation._id);
    const disabled = !!productOverride?.disabled
        ? true
        : productVariationOverride?.disabled ?? packToProductVariation.productVariation.disabled;
    return {
        ...packToProductVariation,
        productVariation: {
            ...packToProductVariation.productVariation,
            disabled
        }
    };
};
const overridePackToStep = ({ packToStep, catalogSnapshotToShop }) => {
    return {
        ...packToStep,
        step: {
            ...packToStep.step,
            stepToProductVariations: packToStep.step.stepToProductVariations.map(stepToProductVariation => {
                const productOverride = catalogSnapshotToShop?.data?.productsOverrides.find(productsOverride => productsOverride.productId === stepToProductVariation.productVariation.productId);
                const productVariationOverride = productOverride?.productVariations.find(productVariationOverride => productVariationOverride.productVariationId === stepToProductVariation.productVariation._id);
                const disabled = !!productOverride?.disabled
                    ? true
                    : productVariationOverride?.disabled ?? stepToProductVariation.productVariation.disabled;
                return {
                    ...stepToProductVariation,
                    productVariation: {
                        ...stepToProductVariation.productVariation,
                        disabled
                    }
                };
            })
        }
    };
};
const overridePack = ({ pack, catalogSnapshotToShop }) => {
    const packsOverride = catalogSnapshotToShop?.data?.packsOverrides.find(packsOverride => packsOverride.packId === pack._id);
    const packToSteps = pack?.packToSteps || [];
    const packToProductVariations = pack?.packToProductVariations || [];
    return {
        ...pack,
        disabled: packsOverride?.disabled ?? pack?.disabled,
        price: packsOverride?.price ?? pack?.price,
        deliveryPrice: packsOverride?.deliveryPrice ?? packsOverride?.price ?? pack?.deliveryPrice,
        takeAwayPrice: packsOverride?.takeAwayPrice ?? packsOverride?.price ?? pack?.takeAwayPrice,
        onSpotPrice: packsOverride?.onSpotPrice ?? packsOverride?.price ?? pack?.onSpotPrice,
        packToSteps: packToSteps.map(packToStep => overridePackToStep({ packToStep, catalogSnapshotToShop })),
        packToProductVariations: packToProductVariations.map(packToProductVariation => overridePackToProductVariation({ packToProductVariation, catalogSnapshotToShop })),
    };
};
const overridePromotion = ({ promotion, catalogSnapshotToShop }) => {
    const promotionsOverride = catalogSnapshotToShop?.data?.promotionsOverrides.find(promotionsOverride => promotionsOverride.promotionId === promotion._id);
    if (!promotionsOverride) {
        return promotion;
    }
    return {
        ...promotion,
        disabled: promotionsOverride.disabled ?? promotion?.disabled,
    };
};
const filterProducts = ({ catalogSnapshot, products }) => {
    const productsIds = catalogSnapshot.data.catalog.catalogToProducts.map(catalogToProduct => catalogToProduct.productId);
    return products.filter(product => productsIds.includes(product._id));
};
const filterPacks = ({ catalogSnapshot, packs }) => {
    const packsIds = catalogSnapshot.data.catalog.catalogToPacks.map(catalogToProduct => catalogToProduct.packId);
    return packs.filter(pack => packsIds.includes(pack._id));
};
const filterPromotions = ({ catalogSnapshot, promotions }) => {
    const promotionsIds = catalogSnapshot.data.catalog.catalogToPromotions.map(catalogToPromotion => catalogToPromotion.promotionId);
    return promotions.filter(promotion => promotionsIds.includes(promotion._id));
};
const filterTags = ({ catalogSnapshot, tags, filterChildrens = true }) => {
    const tagsIds = catalogSnapshot.data.catalog.catalogToTags.map(catalogToTag => catalogToTag.tagId);
    return tags
        .filter(tag => !filterChildrens || !tag.parentTagId)
        .filter(tag => tagsIds.includes(tag._id))
        .map(tag => {
        const childrens = exports.catalogSnapshotFilters.filterTags({ catalogSnapshot, tags: tag.childrens || [], filterChildrens: false });
        return {
            ...tag,
            childrens,
        };
    });
};
const isCatalogSnapshotWithHubrise = (catalogSnapshot) => {
    if (!catalogSnapshot || !catalogSnapshot?.connector) {
        return false;
    }
    return !!catalogSnapshot.connector.hubriseCatalogId && catalogSnapshot.connector.type === connector_interface_1.ConnectorTypes.HUBRISE;
};
exports.isCatalogSnapshotWithHubrise = isCatalogSnapshotWithHubrise;
exports.catalogSnapshotFilters = {
    filterTags,
    filterPacks,
    filterProducts,
    filterPromotions,
};
exports.catalogSnapshotOverriders = {
    overridePack,
    overrideProduct,
    overridePromotion,
    overrideProductVariation,
};
exports.catalogSnapshotCreators = {
    createTags,
    createPack,
    createPacks,
    createOptions,
    createProduct,
    createProducts,
    createPromotion,
    createPromotions,
    createProductVariations
};
